import { Box, Flex, Grid, Heading, Text } from "@chakra-ui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiBookOpen,
  FiCpu,
  FiDatabase,
  FiHardDrive,
  FiUserCheck,
  FiWifi,
} from "react-icons/fi"

import ConfirmDialog from "@/components/Common/ConfirmDialog"
import { IconBox, VisionCard } from "@/components/VisionUI"
import useAuth from "@/hooks/useAuth"
import { useDemoMode } from "@/hooks/useDemoMode"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin")({
  component: Admin,
})

// Cards greyed out while demo mode is on. Showing the tile (rather
// than hiding it) makes the policy legible — "this exists, but the
// sandbox makes it unsafe right now". Clicking a restricted card now
// opens a dialog that sends the user to /login to authenticate as an
// administrator; the backend gate (``forbid_in_demo_mode_unless_superuser``)
// lets real superusers through, so a logged-in admin can actually do
// the work on a demo cluster like tapdpp.qdat.io.
// /admin-system is gated in demo mode, so the tiles now folded under it
// (branding, demo mode, IP bans, licenses) are reached only through that gate.
const DEMO_DISABLED_PATHS = new Set<string>([
  "/organisations",
  "/user-management",
  "/admin-system",
])

function Admin() {
  const navigate = useNavigate()
  const sc = useSemanticColors()
  const { t } = useTranslation("admin")
  const { enabled: demoEnabled } = useDemoMode()
  const { logout, user } = useAuth()
  const isSuperuser = !!user?.is_superuser
  const [restrictedCardTitle, setRestrictedCardTitle] = useState<string | null>(
    null,
  )

  // Tile grid for the admin overview. Tags-related and system-level
  // tiles are folded behind two super-menu tiles (/admin-tags and
  // /admin-system) so the overview stays a flat one-screen scan.
  const allCards = [
    {
      title: t("cards.orgs"),
      description: t("cards.orgsDesc"),
      icon: FiDatabase,
      path: "/organisations",
    },
    {
      title: t("cards.users"),
      description: t("cards.usersDesc"),
      icon: FiUserCheck,
      path: "/user-management",
    },
    {
      title: t("cards.readers"),
      description: t("cards.readersDesc"),
      icon: FiWifi,
      path: "/readers",
    },
    {
      title: t("cards.tagsMenu", "Tags"),
      description: t(
        "cards.tagsMenuDesc",
        "Tags, operational templates, categories, and SKUs.",
      ),
      icon: FiCpu,
      path: "/admin-tags",
    },
    {
      title: t("cards.systemMenu", "System Administration"),
      description: t(
        "cards.systemMenuDesc",
        "Database, API, tokens, backups, audit, branding, demo mode, IP bans, and licenses.",
      ),
      icon: FiHardDrive,
      path: "/admin-system",
    },
    {
      title: t("cards.dpp", "Digital Product Passport"),
      description: t(
        "cards.dppDesc",
        "Author passport templates, bind tags, and expose public DPP pages per tag.",
      ),
      icon: FiBookOpen,
      path: "/admin-dpp",
    },
  ]

  const demoDisabledTooltip = t(
    "demoDisabledTooltip",
    "Restricted in demo mode — click for details",
  )

  return (
    <Box>
      <Box mb={8}>
        <Heading size="xl" color={sc.heading} mb={2}>
          {t("title")}
        </Heading>
        <Text color={sc.textMuted}>{t("subtitle")}</Text>
      </Box>

      <Grid
        templateColumns={{
          base: "minmax(0, 1fr)",
          md: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(4, minmax(0, 1fr))",
        }}
        gap={4}
      >
        {allCards.map((card) => {
          const isDemoDisabled =
            demoEnabled && !isSuperuser && DEMO_DISABLED_PATHS.has(card.path)
          return (
            <VisionCard
              key={card.path}
              variant="elevated"
              onClick={
                isDemoDisabled
                  ? () => setRestrictedCardTitle(card.title)
                  : () => navigate({ to: card.path })
              }
              cursor="pointer"
              opacity={isDemoDisabled ? 0.5 : 1}
              transition="all 0.2s"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "xl",
                opacity: isDemoDisabled ? 0.7 : 1,
              }}
              title={isDemoDisabled ? demoDisabledTooltip : undefined}
              aria-disabled={isDemoDisabled || undefined}
            >
              <Flex
                direction="column"
                align="center"
                textAlign="center"
                py={5}
                px={4}
              >
                <IconBox variant="glass" size="lg" mb={3}>
                  <card.icon fontSize="24px" color={sc.iconColor} />
                </IconBox>
                <Heading size="sm" color={sc.heading} mb={1}>
                  {card.title}
                </Heading>
                <Text color={sc.textMuted} fontSize="xs">
                  {card.description}
                </Text>
                {isDemoDisabled && (
                  <Text
                    color={sc.textMuted}
                    fontSize="xs"
                    fontStyle="italic"
                    mt={2}
                  >
                    {demoDisabledTooltip}
                  </Text>
                )}
              </Flex>
            </VisionCard>
          )
        })}
      </Grid>

      <ConfirmDialog
        open={restrictedCardTitle !== null}
        onClose={() => setRestrictedCardTitle(null)}
        onConfirm={() => {
          setRestrictedCardTitle(null)
          logout()
        }}
        title={t(
          "demoDisabledDialog.title",
          "Administrator credentials required",
        )}
        message={t("demoDisabledDialog.body", {
          defaultValue:
            "{{name}} is unavailable while demo mode is active. Sign in with administrator credentials to access it.",
          name: restrictedCardTitle ?? "",
        })}
        confirmLabel={t(
          "demoDisabledDialog.signIn",
          "Sign in as administrator",
        )}
        colorScheme="blue"
      />
    </Box>
  )
}
